import { ReactNode } from "react";

import {
  IBuildRideSupplierHeaderTooltipContentParams,
  IBuildSendStatusTooltipLineParams,
  IResolveRideSupplierSendStatusParams,
  IRideMinimumPassengersInput,
  TRideSupplierSendStatus,
} from "./ride-supplier-header.models";

export type { TRideSupplierSendStatus } from "./ride-supplier-header.models";

export const isRideSendingAllowedByMinimumPassengers = ({
  seatsCount,
  minimumPassengers,
}: IRideMinimumPassengersInput): boolean => {
  if (!minimumPassengers || minimumPassengers <= 0) {
    return true;
  }

  return (seatsCount ?? 0) >= minimumPassengers;
};

export const resolveRideSupplierSendStatus = ({
  seatsCount,
  minimumPassengers,
  canSendBelowMinimumByPermission,
}: IResolveRideSupplierSendStatusParams): TRideSupplierSendStatus => {
  if (!minimumPassengers || minimumPassengers <= 0) {
    return "hidden";
  }

  if ((seatsCount ?? 0) >= minimumPassengers) {
    return "allowed";
  }

  if (canSendBelowMinimumByPermission) {
    return "allowedByPermission";
  }

  return "blocked";
};

export const shouldShowRideSupplierSendStatusIcon = (
  status: TRideSupplierSendStatus,
): boolean => status === "blocked" || status === "allowedByPermission";

const buildSendStatusTooltipLine = ({
  sendStatus,
  minimumPassengers,
  t,
}: IBuildSendStatusTooltipLineParams): ReactNode => {
  if (sendStatus === "allowed") {
    return (
      <div key="send-allowed">
        {t("ride.supplierTooltip.minPassengersForSend", {
          minimumPassengers,
        })}
      </div>
    );
  }

  if (sendStatus === "allowedByPermission") {
    return (
      <div key="send-allowed-by-permission">
        {t("ride.supplierTooltip.minPassengersForSendAllowedByPermission", {
          minimumPassengers,
        })}
      </div>
    );
  }

  if (sendStatus === "blocked") {
    return (
      <div key="send-blocked">
        {t("ride.supplierTooltip.minPassengersForSendBlocked", {
          minimumPassengers,
        })}
      </div>
    );
  }

  return null;
};

export const buildRideSupplierHeaderTooltipContent = ({
  rideDto,
  t,
  canSendBelowMinimumByPermission,
}: IBuildRideSupplierHeaderTooltipContentParams): ReactNode => {
  const supplierName = rideDto.supplierName || t("general.NO_NAME");
  const minimumPassengers = rideDto.rideTypeMinimumPassengersCountForSent;
  const seatsCount = rideDto.metadata?.seatsCount;
  const sendStatus = resolveRideSupplierSendStatus({
    seatsCount,
    minimumPassengers,
    canSendBelowMinimumByPermission: !!canSendBelowMinimumByPermission,
  });

  const freezeTimeLines: ReactNode[] = [];

  if (rideDto.supplierFreezeTimeToCreate) {
    freezeTimeLines.push(
      <div key="freeze-create">
        {t("supplier.formLabel.freezeTimeToCreateRide")}:{" "}
        {rideDto.supplierFreezeTimeToCreate}
      </div>,
    );
  }

  if (rideDto.supplierFreezeTimeToChange) {
    freezeTimeLines.push(
      <div key="freeze-change">
        {t("supplier.formLabel.freezeTimeToChangeRide")}:{" "}
        {rideDto.supplierFreezeTimeToChange}
      </div>,
    );
  }

  if (rideDto.supplierFreezeTimeToCancel) {
    freezeTimeLines.push(
      <div key="freeze-cancel">
        {t("supplier.formLabel.freezeTimeToCancelRide")}:{" "}
        {rideDto.supplierFreezeTimeToCancel}
      </div>,
    );
  }

  const sendStatusLine =
    sendStatus !== "hidden" && minimumPassengers
      ? buildSendStatusTooltipLine({
          sendStatus,
          minimumPassengers,
          t,
        })
      : null;

  if (!freezeTimeLines.length && !sendStatusLine) {
    return supplierName;
  }

  return (
    <>
      <div>{supplierName}</div>
      {sendStatusLine}
      {freezeTimeLines}
    </>
  );
};
